/**
 * EventVenuesPage — public venue profile (e.g. `/events/denver/venues/blanchard-family-wines`).
 *
 * Designed to industry standards (AirBnB-for-venues + Songkick-for-venues):
 *   • Cover hero with photo gallery
 *   • Full venue identity block (name, address, capacity, amenities, hours)
 *   • Upcoming event listing cards (past events excluded by the API)
 *   • Series grouping when 2+ upcoming events share a `series_id`
 *   • Rich JSON-LD: EventVenue + breadcrumb + ItemList of Event
 *   • Map-deeplink CTA and "Other venues in {city}" sidebar
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Building2, MapPin, Users, Calendar, ChevronRight, Loader2, ExternalLink,
  Star, Wifi, Sparkles, Repeat, ArrowLeft, Clock, Image as ImageIcon,
  Info, Compass, Utensils, Ticket, BookOpen, ChevronLeft, Globe, Phone,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import { VenueClaimWidget } from '../components/VenueClaimWidget';
import VenueBookingSheet, { kindFromSource } from '../components/VenueBookingSheet';
import { breadcrumbLD, eventsItemListLD } from '../utils/structuredData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = (typeof window !== 'undefined' && window.location?.origin) || 'https://eventism.com';

const capitalize = (s = '') =>
  String(s).replace(/[-_]/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  } catch { return iso; }
};

const formatTime = (t) => {
  if (!t) return '';
  const [h, m] = String(t).split(':').map(Number);
  if (Number.isNaN(h)) return t;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour12}:${String(m || 0).padStart(2, '0')} ${period}`;
};

export default function EventVenuesPage() {
  const { city = 'denver', venueSlug } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [otherVenues, setOtherVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [galleryIdx, setGalleryIdx] = useState(0);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const [profileRes, otherRes] = await Promise.all([
        axios.get(`${API}/cities/${city}/venues/${venueSlug}`),
        axios.get(`${API}/cities/${city}/venues`, { params: { limit: 16 } }),
      ]);
      setProfile(profileRes.data);
      setOtherVenues((otherRes.data?.venues || []).filter((v) => v.slug !== venueSlug));
    } catch (e) {
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [city, venueSlug]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const venue = profile?.venue;
  const events = profile?.events || [];
  const classicEvents = profile?.classic_events || [];
  const experiences  = profile?.experiences || [];
  const reservations = profile?.reservations || [];
  const articles     = profile?.articles || [];
  const series = profile?.series || [];
  const cityName = useMemo(() => capitalize(city), [city]);
  const venueName = venue?.name || capitalize(venueSlug);

  // ---- Inline booking state -------------------------------------------
  // `bookingTarget = { kind, event }` opens VenueBookingSheet. Cards and
  // the sticky sidebar CTAs all set this — single source of truth keeps
  // the Sheet outside the tabbed scroll area so it stays on-screen.
  const [bookingTarget, setBookingTarget] = useState(null);
  const openBooking = useCallback((source, event) => {
    setBookingTarget({ kind: kindFromSource(source), event });
  }, []);

  // Surface a confirmation toast when Stripe redirects back with
  // ?booked=1 or ?session_id=… on the URL. Single-shot — strip the
  // params immediately so a refresh doesn't re-fire the toast.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const booked = url.searchParams.get('booked');
    const sid = url.searchParams.get('session_id');
    if (booked === '1' || sid) {
      toast.success('Booking confirmed — check your email for details.');
      url.searchParams.delete('booked');
      url.searchParams.delete('session_id');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  // -----------------------------------------------------------------------
  // SEO / JSON-LD
  // -----------------------------------------------------------------------
  const canonicalUrl = `${SITE_URL}/${city}/venues/${venueSlug}`;
  const breadcrumb = breadcrumbLD([
    { name: 'Home', url: '/' },
    { name: 'Events', url: '/events' },
    { name: cityName, url: `/events/${city}` },
    { name: 'Venues', url: `/${city}/venues` },
    { name: venueName, url: `/${city}/venues/${venueSlug}` },
  ]);

  const placeLD = useMemo(() => {
    if (!venue) return null;
    const photos = (venue.photos && venue.photos.length > 0)
      ? venue.photos
      : (venue.cover_photo ? [venue.cover_photo] : []);
    return {
      '@context': 'https://schema.org',
      '@type': 'EventVenue',
      name: venue.name,
      description: venue.description || undefined,
      address: {
        '@type': 'PostalAddress',
        streetAddress: venue.address || undefined,
        addressLocality: venue.city || cityName,
        addressRegion: venue.state || undefined,
        postalCode: venue.zip_code || undefined,
        addressCountry: 'US',
      },
      image: photos.length > 0 ? photos : undefined,
      maximumAttendeeCapacity: venue.capacity_max || undefined,
      amenityFeature: (venue.amenities || []).map((a) => ({
        '@type': 'LocationFeatureSpecification', name: a, value: true,
      })),
      aggregateRating: venue.review_count > 0 ? {
        '@type': 'AggregateRating',
        ratingValue: venue.rating,
        reviewCount: venue.review_count,
      } : undefined,
      url: canonicalUrl,
    };
  }, [venue, cityName, canonicalUrl]);

  const itemListLD = useMemo(() => {
    if (events.length === 0) return null;
    return eventsItemListLD(
      events.map((e) => ({
        name: e.name,
        url: e.url,
        slug: e.slug,
        city: e.city,
        event_date: e.event_date,
        event_time: e.event_time,
        image_url: e.image_url,
        short_description: e.short_description || e.tagline,
        venue: venueName,
      })),
      `Upcoming events at ${venueName}`,
      `/${city}/venues/${venueSlug}`,
    );
  }, [events, venueName, city, venueSlug]);

  // EventSeries LD — only if 2+ upcoming events share a series
  const seriesLD = useMemo(
    () => series.map((s) => ({
      '@context': 'https://schema.org',
      '@type': 'EventSeries',
      name: s.series_name,
      startDate: s.next_event_date,
      location: { '@type': 'EventVenue', name: venueName },
      url: canonicalUrl,
    })),
    [series, venueName, canonicalUrl],
  );

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex justify-center items-start py-20" data-testid="venue-profile-loading">
        <Loader2 className="h-8 w-8 animate-spin text-fuchsia-400" />
      </div>
    );
  }

  if (!venue) {
    return (
      <div className="min-h-screen bg-background pt-20 pb-16" data-testid="venue-profile-404">
        <div className="max-w-3xl mx-auto px-4 text-center py-16">
          <Building2 className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Venue not found</h1>
          <p className="text-muted-foreground mb-6">We couldn&apos;t locate that venue in {cityName}.</p>
          <Button onClick={() => navigate(`/${city}/venues`)}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Browse all {cityName} venues
          </Button>
        </div>
      </div>
    );
  }

  const photos = (venue.photos && venue.photos.length > 0)
    ? venue.photos
    : (venue.cover_photo ? [venue.cover_photo] : []);
  const activePhoto = photos[galleryIdx] || venue.cover_photo;

  const structured = [breadcrumb, placeLD, itemListLD, ...(seriesLD || [])].filter(Boolean);

  return (
    <div className="min-h-screen bg-background pt-20 pb-20" data-testid="venue-profile">
      <SEO
        title={`${venue.name} — Events & Booking | ${cityName} Venue | Eventism`}
        description={
          venue.description
            ? `${venue.description.slice(0, 155)}${venue.description.length > 155 ? '…' : ''}`
            : `Upcoming events and full venue details for ${venue.name} in ${cityName}. ${profile.total_upcoming || 'See the'} upcoming events on the calendar.`
        }
        canonical={canonicalUrl}
        url={canonicalUrl}
        image={activePhoto}
        keywords={[
          venue.name,
          `${venue.name} ${cityName}`,
          `${venue.name} events`,
          `book ${venue.name}`,
          `${cityName} venue`,
          venue.venue_type ? `${venue.venue_type} venue` : 'event venue',
        ].filter(Boolean)}
        structuredData={structured}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-5 flex-wrap" data-testid="venue-profile-breadcrumbs">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link to="/events" className="hover:text-foreground">Events</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link to={`/events/${city}`} className="hover:text-foreground">{cityName}</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link to={`/${city}/venues`} className="hover:text-foreground">Venues</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-foreground truncate max-w-[200px]">{venue.name}</span>
        </nav>

        {/* HERO GALLERY + IDENTITY */}
        <section className="grid lg:grid-cols-3 gap-5 mb-8">
          {/* Photo gallery */}
          <div className="lg:col-span-2 space-y-2" data-testid="venue-gallery">
            <div className="relative aspect-[16/9] rounded-xl overflow-hidden border border-border bg-muted">
              {activePhoto ? (
                <img
                  src={activePhoto}
                  alt={venue.name}
                  className="absolute inset-0 w-full h-full object-cover"
                  width={1280}
                  height={720}
                  loading="eager"
                  data-testid="venue-gallery-main"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <ImageIcon className="h-12 w-12 text-muted-foreground/40" />
                </div>
              )}
              {venue.is_featured && (
                <Badge className="absolute top-4 left-4 bg-amber-500 text-black border-0">
                  <Sparkles className="h-3 w-3 mr-1" /> Featured venue
                </Badge>
              )}
            </div>
            {photos.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1" data-testid="venue-gallery-thumbs">
                {photos.map((src, i) => (
                  <button
                    key={src + i}
                    onClick={() => setGalleryIdx(i)}
                    className={`flex-shrink-0 w-20 h-14 rounded-md overflow-hidden border-2 transition ${
                      i === galleryIdx ? 'border-fuchsia-400' : 'border-transparent hover:border-border'
                    }`}
                    data-testid={`venue-gallery-thumb-${i}`}
                    aria-label={`Show photo ${i + 1}`}
                  >
                    <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Identity column */}
          <aside className="space-y-3" data-testid="venue-identity">
            {venue.venue_type ? (
              <Link
                to={`/${city}/venues?type=${encodeURIComponent(venue.venue_type)}`}
                className="text-[11px] uppercase tracking-[0.25em] font-bold text-fuchsia-400 hover:text-fuchsia-300 inline-flex items-center gap-2 group transition-colors"
                data-testid="venue-kicker-link"
                title={`See all ${capitalize(venue.venue_type)}s in ${cityName}`}
              >
                <Building2 className="h-3.5 w-3.5" />
                <span className="border-b border-transparent group-hover:border-fuchsia-300">
                  {capitalize(venue.venue_type)}s in {cityName}
                </span>
              </Link>
            ) : (
              <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-fuchsia-400 flex items-center gap-2">
                <Building2 className="h-3.5 w-3.5" /> Event venue
              </p>
            )}
            <h1 className="text-3xl sm:text-4xl font-bold font-['Space_Grotesk'] leading-tight" data-testid="venue-profile-h1">
              {venue.name}
            </h1>
            <VenueClaimWidget city={city} venueSlug={venueSlug} />
            {venue.review_count > 0 && (
              <div className="flex items-center gap-2 text-sm" data-testid="venue-rating">
                <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                <strong>{venue.rating.toFixed(1)}</strong>
                <span className="text-muted-foreground">({venue.review_count} reviews)</span>
              </div>
            )}
            {venue.address && (
              <p className="text-sm text-muted-foreground flex items-start gap-2" data-testid="venue-address">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-indigo-400" />
                <span>
                  {venue.address}<br />
                  {[venue.city || cityName, venue.state, venue.zip_code].filter(Boolean).join(', ')}
                </span>
              </p>
            )}
            {(venue.capacity_min || venue.capacity_max) && (
              <p className="text-sm flex items-center gap-2" data-testid="venue-capacity">
                <Users className="h-4 w-4 text-amber-400" />
                <span>
                  Capacity <strong>{venue.capacity_min}{venue.capacity_max ? `–${venue.capacity_max}` : '+'}</strong> guests
                </span>
              </p>
            )}
            {(venue.website || venue.phone || venue.instagram || venue.facebook) && (
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm" data-testid="venue-contact-links">
                {venue.website && (
                  <a href={venue.website.startsWith('http') ? venue.website : `https://${venue.website}`} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1.5 text-indigo-300 hover:underline" data-testid="venue-website-link">
                    <Globe className="h-4 w-4" /> Website
                  </a>
                )}
                {venue.phone && (
                  <a href={`tel:${venue.phone}`} className="flex items-center gap-1.5 text-indigo-300 hover:underline" data-testid="venue-phone-link">
                    <Phone className="h-4 w-4" /> {venue.phone}
                  </a>
                )}
                {venue.instagram && (
                  <a href={venue.instagram.startsWith('http') ? venue.instagram : `https://instagram.com/${venue.instagram.replace('@', '')}`}
                    target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-indigo-300 hover:underline" data-testid="venue-instagram-link">
                    <ExternalLink className="h-4 w-4" /> Instagram
                  </a>
                )}
                {venue.facebook && (
                  <a href={venue.facebook.startsWith('http') ? venue.facebook : `https://${venue.facebook}`} target="_blank" rel="noreferrer"
                    className="flex items-center gap-1.5 text-indigo-300 hover:underline" data-testid="venue-facebook-link">
                    <ExternalLink className="h-4 w-4" /> Facebook
                  </a>
                )}
              </div>
            )}
            <p className="text-sm flex items-center gap-2" data-testid="venue-upcoming-count">
              <Calendar className="h-4 w-4 text-fuchsia-400" />
              <strong>{profile.total_upcoming}</strong>
              <span className="text-muted-foreground">upcoming {profile.total_upcoming === 1 ? 'event' : 'events'} at this venue</span>
            </p>

            <div className="flex flex-wrap gap-2 pt-2">
              {venue.address && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(
                    `https://maps.google.com/?q=${encodeURIComponent(`${venue.address} ${venue.city || cityName} ${venue.state || ''}`)}`,
                    '_blank',
                  )}
                  data-testid="venue-directions-btn"
                >
                  <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Directions
                </Button>
              )}
            </div>
          </aside>
        </section>

        {/* Series banner — surfaces when 2+ upcoming events share a series */}
        {series.length > 0 && (
          <Card className="mb-6 border-fuchsia-400/30 bg-fuchsia-500/5" data-testid="venue-series-banner">
            <CardContent className="p-4 flex items-start gap-3">
              <div className="w-9 h-9 rounded-md bg-fuchsia-500/20 flex items-center justify-center flex-shrink-0">
                <Repeat className="h-4 w-4 text-fuchsia-400" />
              </div>
              <div className="flex-1 space-y-1.5">
                <p className="text-xs uppercase tracking-wider text-fuchsia-400 font-bold">Recurring series at this venue</p>
                <div className="flex flex-wrap gap-2">
                  {series.map((s) => (
                    <Badge key={s.series_id || s.series_name} className="bg-background border border-fuchsia-400/40 text-foreground" data-testid={`venue-series-${s.series_id || s.series_name}`}>
                      {s.series_name} · {s.event_count} dates
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* MAIN — DesignMyNight-style sticky tab nav + sections.
            Each section gets a stable anchor id; the tab nav scrolls
            into view on click and highlights the active section via
            IntersectionObserver. Conditional tab rendering means a
            venue with no Experiences / Reservations / Menus / Private
            Hire never shows an empty tab — the page reads tight no
            matter how much data the venue has on file. */}
        <VenueTabbedContent
          venue={venue}
          city={city}
          cityName={cityName}
          classicEvents={classicEvents}
          experiences={experiences}
          reservations={reservations}
          events={events}
          total_upcoming={profile.total_upcoming}
          onBook={openBooking}
        />

        {/* "Featured in" — editorial guides this venue appears in,
            sorted by popularity. The flywheel: every guide deepens
            this venue's authority + sends another batch of buyers
            back to "Book Here". */}
        {articles.length > 0 && (
          <FeaturedInCarousel articles={articles} city={city} />
        )}

        {/* Popular venues carousel — bottom of page, full-width */}
        {otherVenues.length > 0 && (
          <PopularVenuesCarousel
            cityName={cityName}
            city={city}
            venues={otherVenues}
          />
        )}
      </div>

      {/* Mobile sticky booking CTA — surfaces whenever the venue has at
          least one bookable offering. Tapping it opens the same sheet
          pre-targeted at the soonest upcoming offering so the booker
          gets to "select details" in one tap. */}
      {(classicEvents[0] || experiences[0] || reservations[0]) && (
        <MobileBookingBar
          classicEvents={classicEvents}
          experiences={experiences}
          reservations={reservations}
          onBook={openBooking}
        />
      )}

      <VenueBookingSheet
        open={!!bookingTarget}
        onOpenChange={(o) => { if (!o) setBookingTarget(null); }}
        target={bookingTarget}
      />
    </div>
  );
}

/**
 * MobileBookingBar — sticky bottom CTA on mobile.
 * Chooses the soonest upcoming offering and surfaces a kind-aware label
 * ("Get Tickets" / "Reserve Experience" / "Book a Table"). Desktop
 * users get the sidebar booking widget instead.
 */
function MobileBookingBar({ classicEvents, experiences, reservations, onBook }) {
  // Soonest of any kind wins the primary CTA. Pricing snapshot kept
  // simple — show "from $X" when we have it.
  const candidate = useMemo(() => {
    const pool = [
      ...classicEvents.map((e) => ({ source: 'classic_events', e })),
      ...experiences.map((e)  => ({ source: 'experiences', e })),
      ...reservations.map((e) => ({ source: 'reservations', e })),
    ];
    pool.sort((a, b) => (a.e.event_date || '').localeCompare(b.e.event_date || ''));
    return pool[0];
  }, [classicEvents, experiences, reservations]);
  if (!candidate) return null;
  const label = candidate.source === 'experiences' ? 'Reserve Experience'
    : candidate.source === 'reservations' ? 'Book a Table'
    : 'Get Tickets';
  const Icon = candidate.source === 'experiences' ? Sparkles
    : candidate.source === 'reservations' ? Utensils
    : Ticket;
  const priceHint = candidate.e.ticket_price_min != null && candidate.e.ticket_price_min !== ''
    ? `from $${candidate.e.ticket_price_min}` : null;
  return (
    <div
      className="fixed bottom-0 inset-x-0 z-30 lg:hidden bg-background/95 backdrop-blur border-t border-border p-3 flex items-center gap-3"
      data-testid="venue-mobile-book-bar"
    >
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Next available</p>
        <p className="text-sm font-bold truncate">{candidate.e.name}</p>
        <p className="text-xs text-muted-foreground truncate">
          {formatDate(candidate.e.event_date)}
          {priceHint && <> · <span className="text-fuchsia-400 font-bold">{priceHint}</span></>}
        </p>
      </div>
      <Button
        className="flex-shrink-0 bg-gradient-to-r from-fuchsia-500 to-indigo-500 text-white border-0 font-bold"
        onClick={() => onBook(candidate.source, candidate.e)}
        data-testid="venue-mobile-book-cta"
      >
        <Icon className="h-4 w-4 mr-1.5" /> {label}
      </Button>
    </div>
  );
}

/**
 * VenueTabbedContent — DesignMyNight-style page body.
 *
 * Sticky tab nav under the photo hero, anchor-linked sections below.
 * Tabs are conditional: a venue with no Experiences / Reservations /
 * Menus / Private Hire never shows an empty tab. Active tab tracks
 * the section currently in view via IntersectionObserver.
 */
function VenueTabbedContent({ venue, city, cityName, classicEvents, experiences, reservations, events, total_upcoming, onBook }) {
  // Sticky offset accounts for the global navbar (h-16 → 64px) plus a
  // 4px breath. If you change the navbar height, update this.
  const STICKY_OFFSET = 68;

  // Build the tab list dynamically — order matters (sales-funnel first:
  // Description → What's on → Experiences → Reservations → Info).
  const tabs = React.useMemo(() => {
    const t = [];
    if (venue.description || venue.amenities?.length) {
      t.push({ id: 'description', label: 'Description', icon: Info });
    }
    if (classicEvents.length > 0) {
      t.push({ id: 'whats-on', label: "What's on", icon: Calendar,
               count: classicEvents.length });
    }
    if (experiences.length > 0) {
      t.push({ id: 'experiences', label: 'Experiences', icon: Compass,
               count: experiences.length });
    }
    if (reservations.length > 0) {
      t.push({ id: 'reservations', label: 'Make Reservations', icon: Utensils,
               count: reservations.length });
    }
    // Generic Info catch-all: venue_type, capacity, price_band, vibe_tags
    const hasInfo = venue.venue_type || venue.capacity_max || venue.price_band
                    || (venue.vibe_tags && venue.vibe_tags.length > 0);
    if (hasInfo) t.push({ id: 'info', label: 'Info', icon: Building2 });
    if (venue.operating_hours && Object.keys(venue.operating_hours).length > 0) {
      t.push({ id: 'hours', label: 'Hours', icon: Clock });
    }
    if (venue.address || venue.lat) {
      t.push({ id: 'location', label: 'Location', icon: MapPin });
    }
    if (venue.menus && venue.menus.length > 0) {
      t.push({ id: 'menus', label: 'Menus', icon: BookOpen });
    }
    if (venue.private_hire) {
      t.push({ id: 'private-hire', label: 'Private Hire', icon: Sparkles });
    }
    return t;
  }, [venue, classicEvents.length, experiences.length, reservations.length]);

  const [activeId, setActiveId] = React.useState(tabs[0]?.id || 'description');

  // Scrollspy — observe each section and set activeId to whichever is
  // currently nearest the top of the viewport (below the sticky bar).
  React.useEffect(() => {
    if (!tabs.length) return;
    const sections = tabs
      .map((t) => document.getElementById(`venue-section-${t.id}`))
      .filter(Boolean);
    if (sections.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the topmost visible section.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          const id = visible[0].target.id.replace('venue-section-', '');
          setActiveId(id);
        }
      },
      { rootMargin: `-${STICKY_OFFSET + 40}px 0px -55% 0px`, threshold: 0.01 },
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [tabs]);

  const jumpTo = (id) => {
    const el = document.getElementById(`venue-section-${id}`);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - STICKY_OFFSET;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  if (tabs.length === 0) {
    // Defensive — should never happen since Description is the default
    // when the venue has anything at all. Render the legacy upcoming
    // events grid so the page never goes blank.
    return (
      <section data-testid="venue-section-fallback" className="space-y-3">
        <p className="text-sm text-muted-foreground">
          We don&apos;t have full details for this venue yet.
        </p>
        {events.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.map((e) => <VenueEventCard key={e.event_id} event={e} onBook={() => onBook?.('classic_events', e)} />)}
          </div>
        )}
      </section>
    );
  }

  return (
    <div data-testid="venue-tabbed-content">
      {/* Sticky tab nav */}
      <nav
        className="sticky z-20 bg-background/85 backdrop-blur-md border-b border-border -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8"
        style={{ top: STICKY_OFFSET - 4 }}
        data-testid="venue-tab-nav"
      >
        <div className="flex gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = activeId === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => jumpTo(t.id)}
                className={`flex items-center gap-1.5 px-3 sm:px-4 py-3 text-xs sm:text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${
                  active
                    ? 'text-foreground border-fuchsia-400'
                    : 'text-muted-foreground border-transparent hover:text-foreground hover:border-border'
                }`}
                data-testid={`venue-tab-${t.id}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
                {t.count ? (
                  <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                    active ? 'bg-fuchsia-500/20 text-fuchsia-300' : 'bg-muted text-muted-foreground'
                  }`}>
                    {t.count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Sections — anchor ids match tabs[].id */}
      <div className="space-y-10 mt-8">
        {tabs.find((t) => t.id === 'description') && (
          <section id="venue-section-description" data-testid="venue-section-description" className="scroll-mt-24 space-y-4">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <Info className="h-4 w-4 text-indigo-400" /> About {venue.name}
            </h2>
            {venue.description && (
              <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">
                {venue.description}
              </p>
            )}
            {venue.amenities?.length > 0 && (
              <div className="pt-2">
                <p className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground mb-2">Amenities</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {venue.amenities.map((a) => (
                    <div key={a} className="flex items-center gap-2 text-sm text-muted-foreground py-1.5">
                      <Wifi className="h-3.5 w-3.5 text-indigo-400 flex-shrink-0" />
                      <span>{a}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {classicEvents.length > 0 && (
          <section id="venue-section-whats-on" data-testid="venue-section-whats-on" className="scroll-mt-24">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Calendar className="h-4 w-4 text-fuchsia-400" /> What&apos;s on
              </h2>
              <span className="text-xs text-muted-foreground">
                {classicEvents.length} of {total_upcoming} upcoming
              </span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {classicEvents.map((e) => <VenueEventCard key={e.event_id} event={e} onBook={() => onBook?.('classic_events', e)} />)}
            </div>
          </section>
        )}

        {experiences.length > 0 && (
          <section id="venue-section-experiences" data-testid="venue-section-experiences" className="scroll-mt-24">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Compass className="h-4 w-4 text-amber-400" /> Experiences
              </h2>
              <span className="text-xs text-muted-foreground">
                {experiences.length} bookable
              </span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {experiences.map((e) => <VenueEventCard key={e.event_id} event={e} onBook={() => onBook?.('experiences', e)} />)}
            </div>
          </section>
        )}

        {reservations.length > 0 && (
          <section id="venue-section-reservations" data-testid="venue-section-reservations" className="scroll-mt-24">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Utensils className="h-4 w-4 text-emerald-400" /> Make Reservations
              </h2>
              <span className="text-xs text-muted-foreground">
                {reservations.length} option{reservations.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {reservations.map((e) => <VenueEventCard key={e.event_id} event={e} onBook={() => onBook?.('reservations', e)} />)}
            </div>
          </section>
        )}

        {tabs.find((t) => t.id === 'info') && (
          <section id="venue-section-info" data-testid="venue-section-info" className="scroll-mt-24">
            <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
              <Building2 className="h-4 w-4 text-indigo-400" /> Info
            </h2>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              {venue.venue_type && (
                <div className="flex justify-between border-b border-border/40 py-1.5">
                  <span className="text-muted-foreground">Type</span>
                  <span className="font-medium">{capitalize(venue.venue_type)}</span>
                </div>
              )}
              {venue.price_band && (
                <div className="flex justify-between border-b border-border/40 py-1.5">
                  <span className="text-muted-foreground">Price</span>
                  <span className="font-medium">{venue.price_band}</span>
                </div>
              )}
              {(venue.capacity_min || venue.capacity_max) && (
                <div className="flex justify-between border-b border-border/40 py-1.5">
                  <span className="text-muted-foreground">Capacity</span>
                  <span className="font-medium">
                    {venue.capacity_min}{venue.capacity_max ? `–${venue.capacity_max}` : '+'} guests
                  </span>
                </div>
              )}
              {venue.neighborhood && (
                <div className="flex justify-between border-b border-border/40 py-1.5">
                  <span className="text-muted-foreground">Neighborhood</span>
                  <Link
                    to={`/${city}/venues?neighborhood=${encodeURIComponent(venue.neighborhood)}`}
                    className="font-medium text-fuchsia-300 hover:text-fuchsia-200 hover:underline inline-flex items-center gap-1"
                    data-testid="venue-info-neighborhood-link"
                    title={`Other venues in ${venue.neighborhood}`}
                  >
                    {venue.neighborhood} <ChevronRight className="h-3 w-3" />
                  </Link>
                </div>
              )}
              {venue.website && (
                <div className="flex justify-between border-b border-border/40 py-1.5">
                  <span className="text-muted-foreground">Website</span>
                  <a href={venue.website} target="_blank" rel="noreferrer" className="font-medium text-fuchsia-400 hover:underline inline-flex items-center gap-1">
                    Visit <Globe className="h-3 w-3" />
                  </a>
                </div>
              )}
              {venue.phone && (
                <div className="flex justify-between border-b border-border/40 py-1.5">
                  <span className="text-muted-foreground">Phone</span>
                  <a href={`tel:${venue.phone}`} className="font-medium hover:underline inline-flex items-center gap-1">
                    <Phone className="h-3 w-3" /> {venue.phone}
                  </a>
                </div>
              )}
            </div>
            {venue.vibe_tags?.length > 0 && (
              <div className="mt-4">
                <p className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground mb-2">Vibe</p>
                <div className="flex flex-wrap gap-2">
                  {venue.vibe_tags.map((t) => (
                    <Link
                      key={t}
                      to={`/${city}/venues?tag=${encodeURIComponent(t)}`}
                      data-testid={`venue-vibe-link-${String(t).toLowerCase().replace(/\s+/g, '-')}`}
                      title={`Other venues in ${cityName} with the ${t} vibe`}
                    >
                      <Badge
                        variant="outline"
                        className="text-xs hover:bg-amber-500/10 hover:border-amber-400/50 hover:text-amber-200 transition-colors cursor-pointer"
                      >
                        {t}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {venue.operating_hours && Object.keys(venue.operating_hours).length > 0 && (
          <section id="venue-section-hours" data-testid="venue-section-hours" className="scroll-mt-24">
            <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4 text-indigo-400" /> Hours
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-1 text-sm">
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                const h = venue.operating_hours[day];
                return (
                  <div key={day} className="flex justify-between border-b border-border/40 py-1.5">
                    <span className="capitalize text-muted-foreground">{day}</span>
                    <span>{h?.open ? `${h.open} – ${h.close}` : 'Closed'}</span>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {(venue.address || venue.lat) && (
          <section id="venue-section-location" data-testid="venue-section-location" className="scroll-mt-24 space-y-3">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <MapPin className="h-4 w-4 text-rose-400" /> Location
            </h2>
            {venue.address && (
              <p className="text-sm text-muted-foreground">
                {venue.address}<br />
                {[venue.city || cityName, venue.state, venue.zip_code].filter(Boolean).join(', ')}
              </p>
            )}
            {(venue.lat && venue.lng) && (
              <div className="rounded-xl overflow-hidden border border-border aspect-[16/9] max-h-80 bg-card">
                <iframe
                  title={`${venue.name} on the map`}
                  loading="lazy"
                  src={`https://www.google.com/maps?q=${venue.lat},${venue.lng}&z=15&output=embed`}
                  className="w-full h-full"
                  data-testid="venue-map-embed"
                />
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2" data-testid="venue-section-location-actions">
              <Button
                size="sm"
                variant="outline"
                onClick={() => window.open(
                  `https://maps.google.com/?q=${encodeURIComponent(`${venue.address || ''} ${venue.city || cityName} ${venue.state || ''}`)}`,
                  '_blank',
                )}
                data-testid="venue-section-directions"
              >
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Get directions
              </Button>
              {venue.website && (
                <Button
                  size="sm"
                  className="bg-fuchsia-500 hover:bg-fuchsia-400 text-white"
                  onClick={() => window.open(venue.website, '_blank', 'noopener,noreferrer')}
                  data-testid="venue-section-visit-website"
                >
                  <Globe className="h-3.5 w-3.5 mr-1.5" /> Visit website
                </Button>
              )}
            </div>
          </section>
        )}

        {venue.menus?.length > 0 && (
          <section id="venue-section-menus" data-testid="venue-section-menus" className="scroll-mt-24 space-y-3">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-amber-400" /> Menus
            </h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {venue.menus.map((m, i) => (
                <a
                  key={(m.url || m.name || '') + i}
                  href={m.url || '#'}
                  target={m.url ? '_blank' : undefined}
                  rel="noreferrer"
                  className="flex items-center gap-3 p-3 rounded-xl border border-border hover:border-amber-400/50 transition-colors"
                  data-testid={`venue-menu-${i}`}
                >
                  <BookOpen className="h-4 w-4 text-amber-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{m.name || `Menu ${i + 1}`}</p>
                    {m.description && (
                      <p className="text-xs text-muted-foreground truncate">{m.description}</p>
                    )}
                  </div>
                  {m.url && <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />}
                </a>
              ))}
            </div>
          </section>
        )}

        {venue.private_hire && (
          <section id="venue-section-private-hire" data-testid="venue-section-private-hire" className="scroll-mt-24 space-y-3">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-fuchsia-400" /> Private Hire
            </h2>
            {typeof venue.private_hire === 'string' ? (
              <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">
                {venue.private_hire}
              </p>
            ) : (
              <>
                {venue.private_hire.description && (
                  <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">
                    {venue.private_hire.description}
                  </p>
                )}
                {venue.private_hire.capacity && (
                  <p className="text-sm">
                    <span className="text-muted-foreground">Capacity:</span> <strong>{venue.private_hire.capacity}</strong>
                  </p>
                )}
              </>
            )}
            <Button asChild size="sm" className="bg-fuchsia-500 hover:bg-fuchsia-400 text-white">
              <Link to={`/venues/colorado/${(venue.city || cityName).toLowerCase()}/all/${venue.slug}?inquire=1`}>
                Request a private-hire quote
              </Link>
            </Button>
          </section>
        )}
      </div>
    </div>
  );
}

/**
 * FeaturedInCarousel — "As featured in" rail showing every editorial
 * guide that includes this venue, sorted by view_count (popularity).
 * This is the flywheel: every guide cited deepens venue authority
 * AND drives buyers back into the booking funnel.
 */
function FeaturedInCarousel({ articles, city }) {
  const scrollerRef = React.useRef(null);
  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.85), behavior: 'smooth' });
  };

  return (
    <section className="mt-14" data-testid="venue-featured-in">
      <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-amber-300 flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" /> As featured in
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk'] mt-1">
            Articles &amp; guides this venue appears in
          </h2>
        </div>
        {articles.length > 2 && (
          <div className="flex items-center gap-2">
            <Button size="icon" variant="outline" onClick={() => scrollBy(-1)} aria-label="Scroll left" data-testid="featured-in-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="outline" onClick={() => scrollBy(1)} aria-label="Scroll right" data-testid="featured-in-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
      <div
        ref={scrollerRef}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 -mx-4 px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid="featured-in-scroller"
      >
        {articles.map((a) => (
          <Link
            key={a.slug}
            to={`/${a.city || city}/guides/${a.slug}`}
            className="group flex-shrink-0 w-[280px] sm:w-[320px] snap-start rounded-xl overflow-hidden border border-border hover:border-amber-400/50 transition-colors bg-card"
            data-testid={`featured-in-card-${a.slug}`}
          >
            <div className="relative aspect-[16/10] bg-gradient-to-br from-amber-500/15 via-fuchsia-500/10 to-indigo-500/15 overflow-hidden">
              {a.cover_image ? (
                <img
                  src={a.cover_image}
                  alt={a.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <BookOpen className="h-10 w-10 text-amber-300/50" />
                </div>
              )}
              {a.view_count > 0 && (
                <span className="absolute bottom-2 left-2 text-[10px] uppercase tracking-widest font-bold bg-background/85 backdrop-blur px-2 py-1 rounded-full text-amber-300 border border-amber-400/30">
                  {a.view_count.toLocaleString()} reads
                </span>
              )}
            </div>
            <div className="p-3">
              <p className="text-[10px] uppercase font-bold tracking-widest text-amber-300 mb-1">
                {a.category_label || 'Editorial guide'}
              </p>
              <p className="font-semibold text-sm leading-snug line-clamp-2 group-hover:text-amber-300 transition-colors">
                {a.title}
              </p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/**
 * PopularVenuesCarousel — horizontal scroll-snapping carousel sorted by
 * upcoming event count. Native scroll-snap keeps the component dependency-
 * free; left/right buttons handle keyboard + click for desktop.
 */
function PopularVenuesCarousel({ cityName, city, venues }) {
  const scrollerRef = React.useRef(null);
  const sorted = React.useMemo(
    () => [...venues].sort(
      (a, b) => (b.upcoming_events_count || 0) - (a.upcoming_events_count || 0),
    ),
    [venues],
  );

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.85), behavior: 'smooth' });
  };

  return (
    <section className="mt-12" data-testid="popular-venues-carousel">
      <div className="flex items-end justify-between mb-4 gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-fuchsia-400 flex items-center gap-2">
            <Building2 className="h-3.5 w-3.5" /> Popular {cityName} venues
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk'] mt-1">
            Where else {cityName} is going out
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="icon"
            variant="outline"
            onClick={() => scrollBy(-1)}
            data-testid="popular-venues-prev"
            aria-label="Scroll left"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="outline"
            onClick={() => scrollBy(1)}
            data-testid="popular-venues-next"
            aria-label="Scroll right"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div
        ref={scrollerRef}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 -mx-4 px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid="popular-venues-scroller"
      >
        {sorted.map((v) => (
          <PopularVenueCard key={v.slug || v.venue_id} venue={v} />
        ))}
      </div>
      <div className="text-center mt-4">
        <Link
          to={`/${city}/venues`}
          className="text-sm text-fuchsia-400 hover:underline inline-flex items-center gap-1"
          data-testid="popular-venues-see-all"
        >
          See all {cityName} venues <ChevronRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </section>
  );
}

function PopularVenueCard({ venue: v }) {
  return (
    <Link
      to={v.url}
      className="group flex-shrink-0 w-[260px] sm:w-[280px] snap-start rounded-xl overflow-hidden border border-border hover:border-fuchsia-400/50 transition-colors bg-card flex flex-col"
      data-testid={`popular-venue-card-${v.slug}`}
    >
      <div className="relative aspect-[16/10] bg-gradient-to-br from-fuchsia-500/15 via-amber-500/10 to-indigo-500/15 overflow-hidden">
        {v.cover_photo ? (
          <img
            src={v.cover_photo}
            alt={v.name}
            loading="lazy"
            width={560}
            height={350}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Building2 className="h-10 w-10 text-muted-foreground/40" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        {v.upcoming_events_count > 0 && (
          <Badge className="absolute top-2.5 right-2.5 bg-fuchsia-500/90 border-0 text-[10px]">
            {v.upcoming_events_count} upcoming
          </Badge>
        )}
      </div>
      <div className="p-3 flex-1 space-y-1.5">
        <h3 className="font-semibold text-sm leading-tight group-hover:text-fuchsia-400 transition-colors line-clamp-2">
          {v.name}
        </h3>
        {v.address && (
          <p className="text-[11px] text-muted-foreground flex items-start gap-1">
            <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" />
            <span className="line-clamp-1">{v.address}</span>
          </p>
        )}
        {(v.capacity_min || v.capacity_max) && (
          <p className="text-[11px] text-muted-foreground flex items-center gap-1">
            <Users className="h-3 w-3" />
            {v.capacity_min}{v.capacity_max ? `–${v.capacity_max}` : '+'} guests
          </p>
        )}
      </div>
    </Link>
  );
}

function VenueEventCard({ event: e, onBook }) {
  // Kind-aware CTA: the venue page passes onBook with the source array
  // baked into the closure, so we just need to vary the label/icon here
  // based on what the card represents. Falls back to "Get Tickets" for
  // legacy events when onBook is undefined.
  const ctaLabel = e.kind === 'experience' ? 'Reserve Experience'
    : e.kind === 'reservation' ? 'Book now'
    : 'Get Tickets';
  const CtaIcon = e.kind === 'experience' ? Sparkles
    : e.kind === 'reservation' ? Utensils
    : Ticket;
  // Tock-style "Prepaid for parties of X to Y" subtitle on reservation
  // offers. Built from the event metadata we already expose.
  const isReservation = e.kind === 'reservation';
  const depositCents = Number(e.deposit_amount_cents) || 0;
  const partyMin = Number(e.party_size_min) || 1;
  const partyMax = Number(e.party_size_max) || 8;
  const largeThreshold = Number(e.large_party_threshold) || 0;
  const reservationSubtitle = isReservation
    ? `${depositCents > 0 ? 'Prepaid reservation' : 'Reservation'} for parties of ${partyMin} to ${partyMax}`
    : null;
  // Tock writes "$X per person" only when the deposit applies to every
  // booking. When a threshold is set, surface it explicitly so a 2-top
  // booker doesn't think they're charged $X each.
  const pricePerPerson = isReservation && depositCents > 0
    ? (largeThreshold
        ? `$${(depositCents / 100).toFixed(0)}/person for parties of ${largeThreshold}+`
        : `$${(depositCents / 100).toFixed(0)} per person`)
    : null;
  return (
    <div
      className="group rounded-xl border border-border overflow-hidden hover:border-fuchsia-400/50 transition-colors bg-card flex flex-col"
      data-testid={`venue-event-card-${e.slug}`}
    >
      <Link to={e.url} className="block">
        <div className="relative aspect-[16/10] bg-muted overflow-hidden">
          {e.image_url ? (
            <img src={e.image_url} alt={e.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" loading="lazy" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500/15 via-amber-500/10 to-indigo-500/15" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          {e.is_featured && (
            <Badge className="absolute top-2.5 left-2.5 bg-amber-500 text-black border-0 text-[10px]">
              <Sparkles className="h-3 w-3 mr-1" /> Featured
            </Badge>
          )}
          {e.is_series && (
            <Badge className="absolute top-2.5 right-2.5 bg-fuchsia-500/90 border-0 text-[10px]" data-testid={`event-series-badge-${e.slug}`}>
              <Repeat className="h-3 w-3 mr-1" /> Series
            </Badge>
          )}
        </div>
        <div className="p-3 space-y-1.5">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-fuchsia-400 font-bold">
            {e.category && <span>{e.category}</span>}
            {e.age_restriction && <span className="text-muted-foreground">· {e.age_restriction}</span>}
          </div>
          <h3 className="font-semibold text-sm leading-snug line-clamp-2 group-hover:text-fuchsia-400 transition-colors">
            {e.name}
          </h3>
          {e.tagline && (
            <p className="text-xs text-muted-foreground line-clamp-2">{e.tagline}</p>
          )}
          {isReservation ? (
            <>
              {/* Tock-style description — short_description / tagline are
                  what the venue admin writes in the dashboard's Settings
                  tab. Show the longer one if both exist, else fall back. */}
              {(e.short_description || e.tagline) && (
                <p className="text-xs text-muted-foreground line-clamp-2 pt-1" data-testid={`reservation-desc-${e.slug}`}>
                  {e.short_description || e.tagline}
                </p>
              )}
              <p className="text-xs text-muted-foreground pt-1" data-testid={`reservation-subtitle-${e.slug}`}>
                {reservationSubtitle}
              </p>
              {pricePerPerson && (
                <p className="text-xs text-fuchsia-400 font-bold">{pricePerPerson}</p>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {formatDate(e.event_date)}
                </span>
                {e.event_time && <span>{formatTime(e.event_time)}</span>}
              </div>
              {e.ticket_price_min != null && e.ticket_price_min !== '' && (
                <p className="text-xs text-fuchsia-400 font-bold pt-1">From ${e.ticket_price_min}</p>
              )}
            </>
          )}
        </div>
      </Link>
      {/* Book CTA — sibling to the Link so clicking it doesn't navigate
          to the detail page. Visitor can complete the entire booking in
          the side sheet without leaving the venue page. */}
      {onBook && (
        <div className="p-3 pt-0 mt-auto">
          <Button
            size="sm"
            className="w-full h-9 bg-gradient-to-r from-fuchsia-500 to-indigo-500 text-white border-0 font-bold text-xs"
            onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); onBook(); }}
            data-testid={`venue-card-book-${e.slug}`}
          >
            <CtaIcon className="h-3.5 w-3.5 mr-1.5" /> {ctaLabel}
          </Button>
        </div>
      )}
    </div>
  );
}
